import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkles, Palette, ShoppingBag, Heart, Bell, Wand2, Trophy,
  Package, Users, Crown, Gift, Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { useLanguage } from '@/context/LanguageContext';
import api, { assetUrl } from '@/lib/api';
import SEO from '@/components/SEO';

const STATUS_COLORS = {
  placed: 'text-sky-400',
  in_production: 'text-amber-400',
  shipped: 'text-indigo-400',
  delivered: 'text-emerald-400',
  cancelled: 'text-rose-400',
};

const PRODUCTS = [
  { title: 'Phone Cases', img: 'https://images.unsplash.com/photo-1535469145415-8e49c30eae75?w=800&q=80', to: '/shop/phone_case', fromUsd: 19.99 },
  { title: 'Stickers', img: 'https://images.unsplash.com/photo-1627618997007-6d81f1efb811?w=800&q=80', to: '/shop/sticker', fromUsd: 3.99 },
  { title: 'Labels', img: 'https://images.unsplash.com/photo-1706111577676-4e509572b8af?w=800&q=80', to: '/shop/label', fromUsd: 3.99 },
];

const greetingForNow = (t) => {
  const h = new Date().getHours();
  if (h < 5) return t('signed.greetingNight', 'Hey there, ');
  if (h < 12) return t('signed.greetingMorning', 'Good morning, ');
  if (h < 17) return t('signed.greetingAfternoon', 'Good afternoon, ');
  if (h < 21) return t('signed.greetingEvening', 'Good evening, ');
  return t('signed.greetingNight', 'Hey there, ');
};

// ---- Sub-components (hoisted out of render to avoid focus/remount issues) ----

const StatTile = ({ icon: Icon, label, value, to, testId, accent }) => {
  const inner = (
    <Card
      className="jw-card p-4 sm:p-5 flex items-center gap-3 hover:opacity-95 transition group h-full"
      data-testid={testId}
    >
      <div
        className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${accent || 'jw-gradient-bg'} text-white shadow-md`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--jw-text-subtle)' }}>{label}</p>
        <p className="font-display text-xl font-bold jw-text-primary truncate">{value}</p>
      </div>
      <ArrowRight className="ml-auto w-4 h-4 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
    </Card>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
};

const SectionHeader = ({ title, sub, to, cta = 'See all', testId }) => (
  <div className="flex items-end justify-between gap-4 mb-5">
    <div>
      <h2 className="font-display text-2xl sm:text-3xl font-bold jw-text-primary">{title}</h2>
      {sub && <p className="text-sm mt-1" style={{ color: 'var(--jw-text-muted)' }}>{sub}</p>}
    </div>
    {to && (
      <Link
        to={to}
        data-testid={testId}
        className="text-sm inline-flex items-center gap-1 shrink-0"
        style={{ color: 'var(--jw-text-muted)' }}
      >
        {cta} <ArrowRight className="w-4 h-4" />
      </Link>
    )}
  </div>
);

const TrendingCard = ({ item }) => {
  const { format } = useCurrency();
  const { t } = useLanguage();
  const isDesign = item.entity_type === 'design';
  const to = isDesign ? `/designs/${item.id}` : `/product/${item.id}`;
  return (
    <Link to={to} data-testid={`signed-home-trending-${item.entity_type}-${item.id}`}>
      <Card className="jw-card overflow-hidden group h-full">
        <div className="aspect-[4/3] overflow-hidden relative p-3" style={{ background: 'var(--jw-bg-elev)' }}>
          {item.image_url ? (
            <img
              src={assetUrl(item.image_url)}
              alt={item.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div
              className="w-full h-full flex flex-col items-center justify-center gap-2 p-3 text-center"
              style={{ background: 'linear-gradient(135deg, rgba(230,36,138,0.18), rgba(255,165,0,0.18))' }}
            >
              <Sparkles className="w-6 h-6 opacity-70" />
              <p className="text-xs jw-text-primary line-clamp-2 font-semibold">{item.title}</p>
              <p className="text-[10px]" style={{ color: 'var(--jw-text-muted)' }}>{item.rating_count} {t('signed.ratings', 'ratings')}</p>
            </div>
          )}
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold text-white"
            style={{ background: 'linear-gradient(135deg,#E6248A,#FF6B35)' }}>
            <Trophy className="w-3 h-3" /> {item.rating_avg?.toFixed(1)}
          </div>
        </div>
        <div className="p-3">
          <p className="text-sm font-semibold jw-text-primary truncate">{item.title}</p>
          <div className="flex items-center justify-between mt-1 text-xs" style={{ color: 'var(--jw-text-muted)' }}>
            <span>{isDesign ? t('signed.designBadge', 'Design') : t('signed.productBadge', 'Product')}</span>
            <span>
              {item.price != null ? format(item.price) : `${item.rating_count} ${t('signed.ratings', 'ratings')}`}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  );
};

const OrderRow = ({ order }) => {
  const { format } = useCurrency();
  const { t } = useLanguage();
  const color = STATUS_COLORS[order.status] || 'text-white/60';
  const statusLabel = {
    placed: t('orders.placed', 'Placed'),
    in_production: t('orders.inProduction', 'In Production'),
    shipped: t('orders.shipped', 'Shipped'),
    delivered: t('orders.delivered', 'Delivered'),
    cancelled: t('orders.cancelled', 'Cancelled'),
  }[order.status] || (order.status || '').replace('_', ' ');
  return (
    <Link to={`/orders/${order.id}`} data-testid={`signed-home-order-${order.id}`}>
      <div className="jw-panel flex items-center gap-4 p-3 sm:p-4 rounded-xl hover:opacity-90 transition">
        <div className="w-10 h-10 rounded-lg jw-gradient-bg grid place-items-center text-white shrink-0">
          <Package className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm jw-text-primary truncate">{order.order_number}</p>
          <p className="text-xs" style={{ color: 'var(--jw-text-subtle)' }}>
            {new Date(order.created_at).toLocaleDateString()} · {order.items?.length || 0} {t('orders.items', 'items')}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-semibold text-sm jw-text-primary">{format(order.total)}</p>
          <p className={`text-xs ${color}`}>{statusLabel}</p>
        </div>
      </div>
    </Link>
  );
};

const CommunityCard = ({ listing }) => {
  const { t } = useLanguage();
  return (
  <Link to={`/community/${listing.id}`} data-testid={`signed-home-community-${listing.id}`}>
    <Card className="jw-card overflow-hidden group h-full">
      <div className="aspect-square overflow-hidden p-2" style={{ background: 'var(--jw-bg-elev)' }}>
        {listing.preview_url || listing.image_url ? (
          <img
            src={assetUrl(listing.preview_url || listing.image_url)}
            alt={listing.title || listing.listing_title || 'Community design'}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full grid place-items-center"><Palette className="w-8 h-8 opacity-30" /></div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold jw-text-primary truncate">{listing.title || listing.listing_title || 'Untitled'}</p>
        <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--jw-text-subtle)' }}>
          {t('community.by', 'by')} {listing.owner?.name || t('community.anonymous', 'Community creator')}
        </p>
      </div>
    </Card>
  </Link>
  );
};

// ---- Main page ----

const SignedInHome = () => {
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [trending, setTrending] = useState([]);
  const [community, setCommunity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [oRes, dRes, fRes, nRes, tRes, cRes] = await Promise.allSettled([
          api.get('/orders'),
          api.get('/user-designs'),
          api.get('/favorites'),
          api.get('/notifications'),
          api.get('/top-rated', { params: { limit: 6, min_ratings: 1 } }),
          api.get('/community-designs', { params: { limit: 4 } }),
        ]);
        if (!alive) return;
        if (oRes.status === 'fulfilled') setOrders(oRes.value.data || []);
        if (dRes.status === 'fulfilled') setDesigns(dRes.value.data || []);
        if (fRes.status === 'fulfilled') setFavorites(fRes.value.data || []);
        if (nRes.status === 'fulfilled') setNotifs(nRes.value.data || []);
        if (tRes.status === 'fulfilled') setTrending((tRes.value.data?.items) || tRes.value.data || []);
        if (cRes.status === 'fulfilled') setCommunity(cRes.value.data?.items || cRes.value.data || []);
      } catch (err) {
        console.error('SignedInHome data load failed', err);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  const firstName = (user?.name || '').split(' ')[0] || 'friend';
  const unreadNotifs = notifs.filter(n => !n.read).length;
  const points = user?.loyalty_points || 0;
  const nextRewardAt = 500;
  const rewardProgress = Math.min(100, Math.round((points % nextRewardAt) / nextRewardAt * 100));
  const isPremium = !!user?.is_premium;

  return (
    <div>
      <SEO title={`Welcome back, ${firstName} — Jwalek`} description="Your personalized Jwalek dashboard: designs, orders, favorites and what's trending." path="/" />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-24 -left-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl" style={{ background: 'var(--jw-gradient)' }} />
        <div className="pointer-events-none absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full opacity-25 blur-3xl" style={{ background: 'linear-gradient(135deg,#E6248A,#FFA500)' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-8 relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary" className="jw-panel jw-text-muted">
              {isPremium ? <Crown className="w-3 h-3 mr-1 inline" /> : <Sparkles className="w-3 h-3 mr-1 inline" />}
              {isPremium ? t('signed.badgePremium', 'Premium member') : t('signed.badgeSignedIn', 'Signed in')}
            </Badge>
            <Badge variant="secondary" className="jw-panel jw-text-muted">
              <Gift className="w-3 h-3 mr-1 inline text-orange-400" /> {points.toLocaleString()} {t('signed.pts', 'pts')}
            </Badge>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4 jw-text-primary"
            data-testid="signed-home-greeting"
          >
            {greetingForNow(t)}
            <bdi className="jw-gradient-text">{firstName}</bdi>{lang === 'ar' ? '' : '.'}
          </motion.h1>
          <p className="jw-text-muted text-base sm:text-lg mt-4 max-w-xl leading-relaxed">
            {orders.length === 0 && designs.length === 0
              ? t('signed.heroCtaNewUser', 'Ready to create your very first piece? Start with a template, jump into the AI generator, or shop what everyone else is loving.')
              : t('signed.heroCtaReturning', "Pick up where you left off — a fresh design, a reorder in one tap, or a jump into what's trending right now.")}
          </p>

          <div className="flex flex-wrap items-center gap-3 mt-7">
            <Link to="/shop">
              <Button data-testid="signed-home-start-designing-button" className="jw-btn-glossy rounded-[14px] h-12 px-6 text-base">
                <Palette className="mr-2 w-4 h-4" /> {t('signed.designSomething', 'Design Something')}
              </Button>
            </Link>
            <Link to="/ai-generator">
              <Button data-testid="signed-home-ai-generator-button" className="jw-btn-gloss-ghost h-12 px-6 rounded-[14px]">
                <Wand2 className="mr-2 w-4 h-4" /> {t('signed.tryAiGenerator', 'Try AI Generator')}
              </Button>
            </Link>
            <Link to="/top-rated">
              <Button data-testid="signed-home-top-rated-button" variant="ghost" className="h-12 px-4 rounded-[14px] jw-text-muted">
                <Trophy className="mr-2 w-4 h-4" /> {t('nav.topRated', 'Top Rated')}
              </Button>
            </Link>
          </div>

          {/* Loyalty progress bar */}
          <div className="mt-8 max-w-xl">
            <div className="flex items-center justify-between text-xs mb-2" style={{ color: 'var(--jw-text-muted)' }}>
              <span>{t('signed.nextRewardAt', 'Next reward at {n} pts').replace('{n}', String(nextRewardAt))}</span>
              <span>{t('signed.ptsToGo', '{n} pts to go').replace('{n}', String(Math.max(0, nextRewardAt - (points % nextRewardAt))))}</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
              <div
                className="h-full jw-gradient-bg transition-all duration-700"
                style={{ width: `${rewardProgress}%` }}
                data-testid="signed-home-loyalty-progress"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-2 pb-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatTile
            icon={Palette}
            label={t('mydesigns.title', 'My designs')}
            value={loading ? '—' : designs.length}
            to="/my-designs"
            testId="signed-home-tile-designs"
          />
          <StatTile
            icon={ShoppingBag}
            label={t('nav.orders', 'Orders')}
            value={loading ? '—' : orders.length}
            to="/orders"
            testId="signed-home-tile-orders"
          />
          <StatTile
            icon={Heart}
            label={t('favorites.title', 'Favorites')}
            value={loading ? '—' : favorites.length}
            to="/favorites"
            testId="signed-home-tile-favorites"
            accent="bg-gradient-to-br from-rose-500 to-pink-600"
          />
          <StatTile
            icon={Bell}
            label={unreadNotifs > 0 ? `${unreadNotifs} ${t('signed.notifications', 'unread')}` : t('notifications.title', 'Notifications')}
            value={loading ? '—' : notifs.length}
            to="/notifications"
            testId="signed-home-tile-notifications"
            accent={unreadNotifs > 0 ? 'bg-gradient-to-br from-orange-500 to-amber-500' : ''}
          />
        </div>
      </section>

      {/* Recent orders */}
      {loading ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <Skeleton className="h-24 w-full rounded-xl" />
        </section>
      ) : orders.length > 0 ? (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="signed-home-recent-orders">
          <SectionHeader title={t('signed.recentOrders', 'Recent orders')} sub={t('signed.recentOrdersSub', 'Track, reorder or download an invoice.')} to="/orders" testId="signed-home-orders-see-all" cta={t('signed.seeAll', 'See all')} />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {orders.slice(0, 3).map(o => <OrderRow key={o.id} order={o} />)}
          </div>
        </section>
      ) : null}

      {/* Trending */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="signed-home-trending">
        <SectionHeader title={t('signed.trending', 'Trending on Jwalek')} sub={t('signed.trendingSub', 'Top-rated products & designs right now.')} to="/top-rated" testId="signed-home-trending-see-all" cta={t('signed.seeAll', 'See all')} />
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="aspect-[4/3] rounded-xl" />)}
          </div>
        ) : trending.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {trending.slice(0, 6).map(tr => <TrendingCard key={`${tr.entity_type}-${tr.id}`} item={tr} />)}
          </div>
        ) : (
          <Card className="jw-card p-8 text-center">
            <Trophy className="w-8 h-8 mx-auto opacity-30" />
            <p className="mt-3 jw-text-muted text-sm">{t('signed.trendingEmpty', 'Ratings are just warming up. Be the first to rate a design.')}</p>
            <Link to="/designs"><Button className="mt-4 jw-btn-gloss-ghost h-9">{t('signed.exploreDesigns', 'Explore designs')}</Button></Link>
          </Card>
        )}
      </section>

      {/* Community fresh */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="signed-home-community">
        <SectionHeader title={t('signed.freshCommunity', 'Fresh from the community')} sub={t('signed.freshCommunitySub', 'Real designs, published by real makers.')} to="/community" testId="signed-home-community-see-all" cta={t('signed.seeAll', 'See all')} />
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="aspect-square rounded-xl" />)}
          </div>
        ) : community.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {community.slice(0, 4).map(c => <CommunityCard key={c.id} listing={c} />)}
          </div>
        ) : (
          <Card className="jw-card p-8 text-center">
            <Users className="w-8 h-8 mx-auto opacity-30" />
            <p className="mt-3 jw-text-muted text-sm">{t('signed.communityEmpty', 'The community feed is quiet today. Publish your first design and start the conversation.')}</p>
            <Link to="/my-designs"><Button className="mt-4 jw-btn-gloss-ghost h-9">{t('signed.goToMyDesigns', 'Go to my designs')}</Button></Link>
          </Card>
        )}
      </section>

      {/* Shop by category */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="signed-home-shop-by-category">
        <SectionHeader title={t('signed.shopByCategory', 'Shop by category')} sub={t('signed.shopByCategorySub', 'Three product lines, unlimited possibilities.')} to="/shop" testId="signed-home-shop-see-all" cta={t('signed.seeAll', 'See all')} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PRODUCTS.map(p => (
            <Link key={p.title} to={p.to} data-testid={`signed-home-category-${p.title.toLowerCase().replace(/\s+/g, '-')}`}>
              <Card className="jw-card overflow-hidden group">
                <div className="aspect-[4/3] overflow-hidden" style={{ background: 'var(--jw-bg-elev)' }}>
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 flex items-center justify-between">
                  <h3 className="font-display text-lg font-semibold jw-text-primary">{t(`productType.${p.title === 'Phone Cases' ? 'phone_case' : p.title === 'Stickers' ? 'sticker' : 'label'}`, p.title)}</h3>
                  <span className="text-sm inline-flex items-center gap-1 jw-text-muted">
                    {t('signed.explore', 'Explore')} <ArrowRight className="w-4 h-4 jw-flip-in-rtl" />
                  </span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Loyalty & Premium CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="relative overflow-hidden rounded-[24px] jw-card p-8 sm:p-10">
          <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full opacity-30 blur-3xl jw-gradient-bg" />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="flex items-center gap-2">
                {isPremium ? <Crown className="w-5 h-5 text-yellow-400" /> : <Star className="w-5 h-5 text-orange-400" />}
                <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'var(--jw-text-muted)' }}>
                  {isPremium ? t('signed.premiumBadgeUnlocked', 'Premium perks unlocked') : t('signed.earnMore', 'You could be earning more')}
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2 jw-text-primary">
                {isPremium
                  ? t('signed.thanksPremium', 'Thanks for being Premium — your next order ships priority.')
                  : t('signed.pointsCountingTitle', '{n} points and counting.').replace('{n}', points.toLocaleString())}
              </h3>
              <p className="jw-text-muted text-sm mt-2 max-w-xl">
                {isPremium
                  ? t('signed.premiumSub', 'Redeem your points for discounts, exclusive templates, or gift them to a friend.')
                  : t('signed.earnSub', 'Every order earns loyalty points. Redeem for discounts, unlock exclusive templates, or upgrade to Premium for priority production.')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/profile">
                <Button data-testid="signed-home-profile-cta" className="jw-btn-glossy rounded-[14px] h-12 px-6 text-base">
                  {isPremium ? t('signed.manageRewards', 'Manage rewards') : t('signed.viewRewards', 'View rewards')} <ArrowRight className="ml-2 w-4 h-4 jw-flip-in-rtl" />
                </Button>
              </Link>
              {!isPremium && (
                <Link to="/profile">
                  <Button data-testid="signed-home-premium-cta" className="jw-btn-gloss-ghost h-12 px-6 rounded-[14px]">
                    <Crown className="mr-2 w-4 h-4" /> {t('signed.goPremium', 'Go Premium')}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SignedInHome;
